// Source-document mapping API — wire types for the per-document
// BookStructure flow.
//
// Mental model (replaces the hardcoded `book-structure.ts` from the
// V1.0 ingestion scaffold):
//
//   1. Admin uploads a SourceDocument (any kind — book, article, blog,
//      regulation).
//   2. Admin clicks "Propose map" → backend extracts text + asks
//      Sonnet 4.6 to detect chapter + sub-section structure → returns
//      a BookStructure in `pending_review` state.
//   3. Admin reviews the proposed map in the SPA tree editor, edits
//      labels / patterns / chapter splits as needed.
//   4. Admin clicks "Approve & ingest" → BookStructure flips to
//      `approved` + SourceDocument's `book_structure_id` references it
//      → ingestion Lambda reads the approved map from Mongo (no
//      hardcoded fallback) → produces document_excerpt KB entries.
//
// Per the [[ingestion-no-hardcoded-maps]] decision from 2026-06-15: the
// codebase no longer ships per-book chapter maps in source. Every
// SourceDocument has its own BookStructure, generated + reviewed at
// upload time.

import type { SourceDocumentId, SourceDocumentLanguage } from './source-documents-api.js';

export type BookStructureId = string;

export type BookStructureStatus = 'pending_review' | 'approved' | 'rejected';

/**
 * Paragraph-level override — surgical edit applied to a sub-section's
 * body at ingest time, without changing the extracted source text.
 *
 * Three kinds, matching the design brief's Range-tier actions:
 *   - `rewrite` replaces one paragraph's text (for example to collapse a
 *     promotional aside into a one-liner that embeds better);
 *     `original_text` is kept so the audit drawer can show the
 *     before/after side by side
 *   - `insert_after` adds authored framing or keywords after one
 *     paragraph (the reviewer's prose, tinted `--accent` in the review
 *     UI so provenance stays legible even in retrieved results)
 *   - `exclude` drops one paragraph from the embed payload entirely
 *     (noise, dates, running headers that leaked through extraction)
 *
 * `paragraph_index` is 0-based inside the sub-section, counting from
 * paragraphs produced by splitting the raw sub-section body on blank
 * lines (`\n{2,}`), same convention the oversized-section auto-splitter
 * already uses. Overrides with out-of-range indices are silently
 * ignored at ingest time (defensive; the source text can shift if
 * extraction is re-run with different stripping options).
 *
 * Section-level `excluded` (above) and paragraph-level `exclude`
 * compose: excluding the whole sub-section short-circuits all its
 * paragraph overrides at ingest time.
 */
export type ParagraphOverride =
  | {
      kind: 'rewrite';
      paragraph_index: number;
      /** The author's replacement text. 1–5000 chars. */
      replacement_text: string;
      /** The original paragraph text at the time of the rewrite, kept
       *  for the audit drawer. Empty string allowed when a rewrite is
       *  applied to a paragraph the UI couldn't resolve (defensive). */
      original_text: string;
    }
  | {
      kind: 'insert_after';
      paragraph_index: number;
      /** Authored content inserted AFTER the target paragraph. 1–5000 chars. */
      inserted_text: string;
    }
  | { kind: 'exclude'; paragraph_index: number };

/**
 * A sub-section inside a chapter — the leaf unit the chunker produces
 * one document_excerpt per.
 */
export interface BookSubSectionDef {
  /** Display label shown in the review tree + used in citations. */
  label: string;
  /**
   * Anchor text — the FIRST 4-8 words of the heading as they appear in
   * the document. Stored as a literal string (not regex) so admins can
   * read + edit it; compiled to a flexible-whitespace, case-insensitive
   * regex at ingestion time. Must be unique within the chapter.
   */
  header_pattern: string;
  /**
   * Estimated token count of the text between this sub-section's header
   * and the next boundary (next sub-section, next chapter, or end of doc).
   * Computed during `propose-map` by measuring the character span between
   * consecutive header offsets and dividing by 4 (rough char-to-token
   * ratio for FR/EN prose). Used by the review UI to flag sections that
   * are too broad (retrieval noise) or too small (missing context).
   * Absent on legacy records + on chapters where the header can't be
   * located in the extracted text (defensive; UI shows "—" then).
   */
  estimated_tokens?: number;
  /**
   * When true, the editor has explicitly decided to leave this sub-section
   * OUT of ingest — chunker skips it, no KB entry is produced, and the
   * two-pane review UI renders the span in a struck-through / muted style
   * so it's visibly distinct from a normal in-ingest section AND from a
   * gap (content in no section at all).
   *
   * Absent → treated as false (default: include in ingest). Persisted on
   * the sub-section doc; the review UI toggles it via the "⊘ Leave out
   * of ingest" action on the doc pane.
   */
  excluded?: boolean;
  /**
   * Paragraph-level overrides applied by the review UI's Range-tier
   * actions (Rewrite / Insert / Exclude). Absent / empty → the chunker
   * emits the sub-section text as-extracted. See {@link ParagraphOverride}.
   */
  paragraph_overrides?: ParagraphOverride[];
}

/** A chapter — the structural unit the chunker scans for first. */
export interface BookChapterDef {
  /** 1-indexed, monotonic. */
  number: number;
  /** Display title. */
  title: string;
  /**
   * Anchor text for the chapter heading. For books with standard
   * "Chapitre N" / "Chapter N" headings, the proposer fills this with
   * that literal. For documents that use custom headings, the
   * proposer extracts the actual heading text.
   */
  header_pattern: string;
  /** Sub-sections in document order. Empty array → chunker treats the whole chapter as one excerpt. */
  sub_sections: BookSubSectionDef[];
  /**
   * Estimated token count of the ENTIRE chapter text (all sub-sections
   * combined). Set on every chapter regardless of whether it has
   * sub-sections. When `sub_sections` is empty this IS the chunk size.
   * Computed during `propose-map` — see BookSubSectionDef.estimated_tokens
   * for methodology.
   */
  estimated_tokens?: number;
}

/**
 * Chunk-size quality tiers used by the review UI to flag sections that
 * are too broad (retrieval noise) or too small (missing context).
 * Numbers are rough char-based estimates (chars/4 ≈ tokens for FR/EN
 * prose) — treat as heuristics, not precise counts.
 *
 * Thresholds picked from RAG best practice for reference/coaching content:
 *   - Ideal chunk size: 300-1500 tokens (one coherent idea per chunk)
 *   - Above ~2000 tokens: retrieval starts averaging multiple concepts
 *   - Above ~3000 tokens: severe dilution + will be auto-split by chunker
 *   - Below ~200 tokens: chunk lacks standalone context
 */
export type ChunkSizeTier = 'tiny' | 'good' | 'large' | 'oversized';

export const CHUNK_SIZE_THRESHOLDS = {
  tiny_max: 200,
  good_max: 1500,
  large_max: 3000,
} as const;

export function classifyChunkSize(estimatedTokens: number | undefined): ChunkSizeTier | null {
  if (estimatedTokens == null) return null;
  if (estimatedTokens < CHUNK_SIZE_THRESHOLDS.tiny_max) return 'tiny';
  if (estimatedTokens <= CHUNK_SIZE_THRESHOLDS.good_max) return 'good';
  if (estimatedTokens <= CHUNK_SIZE_THRESHOLDS.large_max) return 'large';
  return 'oversized';
}

/**
 * BookStructure — the recipe the chunker follows to slice a SourceDocument.
 * One per SourceDocument; lives in Mongo.
 */
export interface BookStructure {
  id: BookStructureId;
  /** FK back to the parent SourceDocument. */
  source_document_id: SourceDocumentId;
  /** Display name — defaults to SourceDocument's title but editable. */
  name: string;
  /** Language of the document — drives ingestion classifier prompt language. */
  language: SourceDocumentLanguage;
  status: BookStructureStatus;
  /** The actual map. Empty array signals "not yet proposed". */
  chapters: BookChapterDef[];
  /**
   * Monotonic revision counter for optimistic concurrency control. Starts
   * at 1 on insert, incremented by every mutation (user PATCH, approve,
   * reject, propose-worker completion). Clients that want conflict
   * detection pass this back as `expectedVersion`; a mismatch returns 409
   * with the current doc as `error.details.current`.
   */
  version: number;
  /** ISO timestamp of when the structure was first created (propose or manual). */
  proposed_at: string;
  /** Cognito sub of the user who triggered propose-map (or hand-authored). */
  proposed_by: string;
  /**
   * Bedrock model ID used by the proposer (e.g.
   * `eu.anthropic.claude-sonnet-4-6`). Null if hand-authored or
   * re-proposed without recording the source.
   */
  proposed_by_model: string | null;
  /**
   * True while the propose-map worker is running Sonnet. The FE polls
   * GET /book-structures/{id} every 2-3s until this flips to false.
   * Set true by POST /propose-map (which 202s); flipped false by the
   * worker after Sonnet returns (success or failure).
   */
  proposing: boolean;
  /**
   * Error message if the last propose attempt failed (Sonnet
   * unreachable, schema validation failed, document extraction blew
   * up). Null on success. Lets the FE show "retry" with context.
   */
  proposing_error: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejected_reason: string | null;
  updated_at: string;
}

// ─── Resolved / derived shapes for the two-pane review UI ─────────────────
//
// The persisted BookStructure carries `header_pattern` per chapter / sub-
// section (a literal anchor string). It does NOT carry character offsets.
// The two-pane review UI needs offsets to render section spans in the
// right-hand source-doc pane (highlighted regions, gap detection, drag-
// to-move boundaries). We RESOLVE offsets on-demand at the API layer
// (see packages/api/src/shared/map-resolver.ts) rather than store them,
// because:
//   - the source text can change if extraction is re-run
//   - the pattern can change if the editor edits it
//   - offset accuracy is easy to verify (recompute) but hard to keep in
//     sync as a stored derived field
//
// The shapes below live on the wire only — Mongo persists the base
// BookChapterDef / BookSubSectionDef and computes ResolvedChapter /
// ResolvedSubSection at read-time.

/**
 * A stretch of the source text NOT covered by any resolved section — i.e.
 * content the reader can see but no chunk will ingest. Renders as a hatched
 * amber block in the right pane with actions to assign it to a new or
 * existing section (or explicitly leave it out).
 *
 * Derived, not persisted. Gaps below a small word-count threshold are
 * suppressed (whitespace, chapter headings themselves, trivial fragments).
 */
export interface MapGap {
  char_start: number;
  /** Exclusive — matches `String.prototype.slice(start, end)` semantics. */
  char_end: number;
  word_count: number;
}

/**
 * A resolved sub-section — the base BookSubSectionDef plus the character
 * offsets its header_pattern matched at inside the source text.
 *
 * When the pattern DIDN'T match (Sonnet proposed a section header that
 * doesn't actually appear in the extracted text — a "boundary issue"),
 * both offsets are null. The UI renders the tree row with an amber
 * "⚠ NO MATCH" pill and a red inline strip in the doc pane at the
 * expected position.
 */
export interface ResolvedSubSection extends BookSubSectionDef {
  char_start: number | null;
  char_end: number | null;
}

/**
 * A resolved chapter — same shape as BookChapterDef but with offsets
 * and resolved sub-sections. `char_end` of a chapter is the char_start
 * of the next resolved chapter (or the document's end).
 */
export interface ResolvedChapter extends Omit<BookChapterDef, 'sub_sections'> {
  char_start: number | null;
  char_end: number | null;
  sub_sections: ResolvedSubSection[];
}

/**
 * The full shape returned by the map-review endpoint: base BookStructure
 * fields, chapters replaced with their resolved forms, plus the source
 * text itself (normalised for match — quote/whitespace forms collapsed
 * to match the offsets) and derived gaps.
 *
 * Frontend renders `source_text` verbatim in the right pane and paints
 * spans / gaps using the offsets. Because we normalise the source text
 * before computing offsets, both the text and the offsets agree
 * character-for-character.
 */
export interface ResolvedBookStructure extends Omit<BookStructure, 'chapters'> {
  chapters: ResolvedChapter[];
  gaps: MapGap[];
  source_text: string;
}

// ─── Wire shapes — requests / responses ───────────────────────────────────

/** POST /source-documents/{id}/propose-map — body is empty (id from path). */
export type ProposeMapRequest = Record<string, never>;
export interface ProposeMapResponse {
  bookStructure: BookStructure;
}

export interface GetBookStructureResponse {
  bookStructure: BookStructure;
}

/**
 * GET /source-documents/{id}/map — the review-mode endpoint. Returns the
 * BookStructure IN ITS RESOLVED FORM (with char offsets + gaps + source
 * text) so the two-pane review UI can render without a second round-trip.
 * The base non-resolved shape stays available at
 * GET /book-structures/{id} for callers that don't need offsets
 * (list pages, propose-map polling loop).
 */
export interface GetSourceMapResponse {
  map: ResolvedBookStructure;
}

/**
 * PATCH /book-structures/{id} — editable fields. Status transitions go
 * through the dedicated approve/reject endpoints, not PATCH.
 *
 * `expectedVersion` enables optimistic concurrency: the client sends the
 * version it last saw; the server rejects (409 CONFLICT) if the stored
 * version has moved on. Omitted → write proceeds without a guard (legacy
 * clients during the MR 3 rollout; log-only warning).
 */
export interface PatchBookStructureRequest {
  name?: string;
  chapters?: BookChapterDef[];
  expectedVersion?: number;
}
export interface PatchBookStructureResponse {
  bookStructure: BookStructure;
}

/** POST /book-structures/{id}/approve — body carries the optimistic
 *  concurrency token. Omitted → unconditional (legacy clients). */
export interface ApproveBookStructureRequest {
  expectedVersion?: number;
}
export interface ApproveBookStructureResponse {
  bookStructure: BookStructure;
}

/** POST /book-structures/{id}/reject — optional reason for the audit
 *  trail + optional expectedVersion for concurrency. */
export interface RejectBookStructureRequest {
  reason?: string;
  expectedVersion?: number;
}
export interface RejectBookStructureResponse {
  bookStructure: BookStructure;
}

/**
 * One row of the mutation audit log for a BookStructure. Written by the
 * backend on every state-changing op (create / patch / propose-complete
 * / propose-reset / approve / reject), served by GET /book-structures/
 * {id}/audit, consumed by the MR 10 history drawer.
 *
 * `before` + `after` carry the full chapters snapshot at the two
 * versions — the drawer can render a diff without replaying intermediate
 * states. `before` is null when `op === 'create'`.
 */
export type BookStructureAuditOp =
  | 'create'
  | 'patch'
  | 'propose-complete'
  | 'propose-reset'
  | 'approve'
  | 'reject';

export interface BookStructureAuditEntry {
  id: string;
  structure_id: BookStructureId;
  from_version: number;
  to_version: number;
  /** Cognito sub for human actions; synthetic 'system:*' sentinel for
   *  propose-worker / invoke-failure writes. */
  actor_sub: string;
  op: BookStructureAuditOp;
  reason: string | null;
  before: BookChapterDef[] | null;
  after: BookChapterDef[];
  created_at: string;
}

/** GET /book-structures/{id}/audit — newest first, capped at 200. */
export interface ListBookStructureAuditResponse {
  entries: BookStructureAuditEntry[];
}
